import _                                                            from 'underscore';
import React, { MutableRefObject, useContext, useEffect, useState } from 'react';

import { usePrevious }                                                                                                       from './usePrevious';
import { useSystemModals, useSystemToasts }                                                                                  from './useSystem';
import { DecisionEngineService }                                                                                             from '../services';
import { CONSTANTS }                                                                                                         from '../constants';
import { AssumptionIdOrFieldName, CorvalIntakeQuestion, CorvalIntakeQuestionCondition, Scalars, UseStateHookFunction, Uuid } from '../types';

interface UseProjectSurveyHook {
	
	/* Method that consumes a GET_PROJECT_ASSUMPTIONS graph response set and
	produces an array a survey can use for layout; sorts by ordinal,
	then by triggering condition relationship */
	createSurveyQuestionSet(
		unansweredQuestions: ProjectSurveyQuestionItem[],
		answeredQuestions: ProjectSurveyQuestionItem[]
	): ProjectSurveyQuestionItem[],
	
	/* Creates an object that contains objective-based and project-based arrays containing */
	partitionQuestionsByObjectiveRelationship( allQuestions: ProjectSurveyQuestionItem[] ): ( {
		objectiveBased: ProjectSurveyQuestionItem[],
		projectBased: ProjectSurveyQuestionItem[],
	} ),
	
	/* Initiates a on-survey-page visual display cue that highlights a specific survey item */
	highlightQuestion( id: AssumptionIdOrFieldName ): void,
	
	/* Generates an html id string for use with the highlightQuestion hook method */
	getHighlightQuestionId( id: AssumptionIdOrFieldName ): string,
	
	/* Updates the total question count recorded in order to trigger a toast message when updated. */
	updateTotalQuestionCount: UseStateHookFunction<number>
}

export interface ProjectSurveyQuestionItem {
	intakeQuestion: Partial<CorvalIntakeQuestion>,
	triggeringCondition?: Partial<CorvalIntakeQuestionCondition> | Partial<CorvalIntakeQuestionCondition>[] | null,
	answerValue?: unknown,
	createdById?: Uuid
	updatedById?: Uuid,
	createdUtc?: Scalars['DateTime']
	updatedUtc?: Scalars['DateTime']
}

const ProjectSurveyRef: MutableRefObject<UseProjectSurveyHook> = React.createRef();

export const createSurveyQuestionSet: UseProjectSurveyHook['createSurveyQuestionSet'] = ( unansweredQuestions = [], assumptions = [] ) => {
	
	let allQuestions: ProjectSurveyQuestionItem[] = [];
	
	// get unanswered questions...
	if( unansweredQuestions.length ){
		unansweredQuestions.map( question => {
			allQuestions.push( {
				intakeQuestion:      question.intakeQuestion,
				answerValue:         null,
				triggeringCondition: question.triggeringCondition
			} )
		} )
	}
	// get assumption questions...
	if( assumptions.length ){
		assumptions.map( assumption => {
			
			// capture triggering conditions that may be involved on this survey
			let conditions = assumption.intakeQuestion.conditions;
			
			allQuestions.push( {
				intakeQuestion:      assumption.intakeQuestion,
				answerValue:         assumption.answerValue,
				updatedById:         assumption.updatedById,
				updatedUtc:          assumption.updatedUtc,
				triggeringCondition: _.isEmpty( conditions ) ? null :
				                     ( _.chain( conditions )
				                        .pluck( 'targetIntakeQuestionId' )
				                        .uniq()
				                        .value() as CorvalIntakeQuestionCondition['targetIntakeQuestionId'][] )
			} )
		} )
	}
	
	// sort by ordinal
	allQuestions = _.sortBy( allQuestions, q => q.intakeQuestion.ordinal );
	
	// assess the need to sort further
	let triggeringConditionsExist = ( _.reject( allQuestions, q => _.isNull( q.triggeringCondition ) ).length > 0 );
	
	// if any follow up questions exist, sort by their relative parent
	// such that they get displayed near the question that triggered it
	if( triggeringConditionsExist ){
		
		let sortedQuestions   = [] as ProjectSurveyQuestionItem[],
		    sortedQuestionIds = [] as Uuid[];
		
		// setup a function to sort children when we find ones relative to the parent
		let sortChildren = ( parentId: Uuid ) => {
			
			return _.map( allQuestions, ( q, index ) => {
				
				if( !_.isNull( q.triggeringCondition ) ){
					
					// if the triggering condition is an array of possible intake questions,
					// inspect if any of those are in this survey, otherwise there is only one trigger id
					let childIdString                   = _.isArray( q.triggeringCondition ) ? q.triggeringCondition.join( ',' ) : q.triggeringCondition.targetIntakeQuestion.id,
					    isChild                         = childIdString.search( parentId ) > -1,
					    sortedQuestionsContainsQuestion = _.contains( sortedQuestionIds, q.intakeQuestion.id );
					
					// if this we have a child, and it's not already accounted for,
					// add into the queue under the parent just
					if( isChild && !sortedQuestionsContainsQuestion ){
						sortedQuestionIds.push( q.intakeQuestion.id );
						sortedQuestions.push( q );
						sortChildren( q.intakeQuestion.id );
					}
				}
				
			} );
		};
		
		// iterate over triggering condition relationships only
		// since verything is sorted by ordinal by default
		_.chain( allQuestions )
		 .filter( q => _.isNull( q.triggeringCondition ) )
		 .map( ( parentQuestion, parentIndex ) => {
			 
			 // add parent question
			 sortedQuestions.push( parentQuestion );
			 
			 // add any children iteratively
			 sortChildren( parentQuestion.intakeQuestion.id );
			 
		 } );
		
		const unSortedQuestions = _.filter( allQuestions, question => !sortedQuestions.map( q => q.intakeQuestion.id ).includes( question.intakeQuestion.id ) );
		
		//reassign question array to the sorted one.
		allQuestions = [ ...sortedQuestions, ...unSortedQuestions ];
	}
	
	return allQuestions;
};

export const partitionQuestionsByObjectiveRelationship: UseProjectSurveyHook['partitionQuestionsByObjectiveRelationship'] = ( allQuestions = [] ) => {
	
	let [ objectiveBased, projectBased ] = _.partition( allQuestions,
		( { intakeQuestion: { questionContext } } ) => !DecisionEngineService.isProjectTypeQuestion( questionContext )
	);
	
	return { objectiveBased, projectBased }
}

export const getHighlightQuestionId = ( intakeQuestionId: AssumptionIdOrFieldName ) => {
	return `survey-item-${ intakeQuestionId }`;
}

export const highlightQuestion = ( intakeQuestionId: AssumptionIdOrFieldName ) => {
	
	window.setTimeout( () => {
		
		let questionFormComponent = document.getElementById( getHighlightQuestionId( intakeQuestionId ) ),
		    blinkClasses          = [
			    'outline-md',
			    'outline--colored-purple',
			    'outline--animated'
		    ];
		
		if( questionFormComponent ){
			
			/* Scroll it into view */
			questionFormComponent.scrollIntoView( { block: "center" } )
			
			/* Get the classes on the component and append the blink classes */
			blinkClasses.map( c => questionFormComponent.classList.add( c ) )
			
			/* Timeout to remove blink classes */
			window.setTimeout( () => {
				
				blinkClasses.map( c => questionFormComponent.classList.remove( c ) )
				
			}, CONSTANTS.UI.DISPLAY.WAIT_SHORT );
		}
		
	}, CONSTANTS.UI.DISPLAY.TRANSITION_DELAY )
}

export const useProjectSurveys = (): UseProjectSurveyHook => {
	
	let toastActions                                     = useSystemToasts(),
	    modalActions                                     = useSystemModals(),
	    [ totalQuestionCount, updateTotalQuestionCount ] = useState<number>( null ),
	    prevTotalQuestionCount: number                   = usePrevious( totalQuestionCount );
	
	// monitor and alert user to survey additions / subtractions
	useEffect( () => {
		
		if( totalQuestionCount && prevTotalQuestionCount ){
			
			if( totalQuestionCount > prevTotalQuestionCount ){
				
				toastActions.info( 'Additional Questions Available', 'One or more questions have been added based on how you answered this survey.' );
				
			} else if( totalQuestionCount < prevTotalQuestionCount ){
				
				toastActions.warning( 'Questions Removed', 'One or more questions have been removed based on how you answered this survey.' );
			}
		}
		
	}, [ totalQuestionCount ] );
	
	ProjectSurveyRef.current = {
		createSurveyQuestionSet,
		partitionQuestionsByObjectiveRelationship,
		highlightQuestion,
		getHighlightQuestionId,
		updateTotalQuestionCount,
	};
	
	return ( ProjectSurveyRef.current )
};
